import React from 'react';
import { motion } from 'framer-motion';
import { Factory, Wrench, Wind, Hammer, Gauge } from 'lucide-react';

const Services = () => {
  const mainServices = [
    {
      icon: Factory,
      title: 'Structural Steel Fabrication',
      items: [
        'Heavy Rolled Sections',
        'Built-up Sections',
        'Furnace Structures (9 MVA / 16 MVA)',
        'Industrial Sheds',
        'Crane Bay Structures',
      ],
      image: 'https://images.pexels.com/photos/53176/architecture-iron-steel-building-53176.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: Wrench,
      title: 'Industrial Erection',
      items: [
        '9 MVA / 16 MVA Furnace Installation',
        'Crane Bay Structures Erection',
        'Rolling Mills Installation',
        'DRI (Direct Reduced Iron) Projects',
        'EOT Crane Erection & Alignment',
      ],
      image: 'https://images.unsplash.com/photo-1738676455943-1ceba4d60ac1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwc3RlZWwlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwd2VsZGluZyUyMGZhYnJpY2F0aW9uJTIwY3JhbmV8ZW58MHx8fHwxNzcyMTc2NDQ4fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Wind,
      title: 'Pollution Control Systems',
      items: [
        'Bag House Installation',
        'FD Cooler Systems',
        'Duct Line Fabrication & Erection',
        'Chimney Construction',
        'Environmental Compliance Solutions',
      ],
      image: 'https://images.pexels.com/photos/4561617/pexels-photo-4561617.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: Hammer,
      title: 'Shutdown & Maintenance',
      items: [
        'Scheduled Plant Shutdowns',
        'Emergency Maintenance Services',
        'Structural Inspection & Repair',
        'Equipment Retrofitting',
        'Preventive Maintenance Programs',
      ],
      image: 'https://images.unsplash.com/photo-1707236527163-bd3478178466?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxzdGVlbCUyMGJlYW1zJTIwY29uc3RydWN0aW9uJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzIxNzY0NjR8MA&ixlib=rb-4.1.0&q=85'
    },
  ];

  const industries = [
    'Steel Plants',
    'Ferro Alloy Units',
    'Sponge Iron Plants',
    'Rolling Mills',
    'Foundries',
    'Industrial Infrastructure',
  ];

  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-block border-l-4 border-orange-500 pl-4 mb-4">
              <p className="text-orange-500 font-oswald tracking-widest text-sm">OUR SERVICES</p>
            </div>
            <h1 className="font-oswald font-bold text-5xl md:text-6xl mb-6">COMPREHENSIVE INDUSTRIAL SOLUTIONS</h1>
            <p className="text-xl text-slate-300 max-w-3xl">
              Complete mechanical construction and structural steel services for heavy industries
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24 bg-white" data-testid="main-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {mainServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`service-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="bg-slate-50 p-8 border-l-4 border-orange-500">
                    <service.icon className="w-16 h-16 text-orange-500 mb-4" />
                    <h2 className="font-oswald font-bold text-3xl text-slate-900 mb-4">{service.title}</h2>
                    <ul className="space-y-3">
                      {service.items.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                          <span className="text-slate-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-[400px] object-cover border-l-4 border-orange-500 shadow-lg"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-24 bg-slate-50" data-testid="industries-served">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-oswald font-bold text-4xl text-slate-900 mb-4">INDUSTRIES WE SERVE</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Trusted partners for leading industrial establishments across India
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white p-6 text-center border-l-4 border-orange-500 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`industry-${index}`}
              >
                <Gauge className="w-12 h-12 text-orange-500 mx-auto mb-3" />
                <p className="font-oswald font-bold text-sm text-slate-900">{industry}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white text-center" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-oswald font-bold text-4xl mb-6">NEED A CUSTOM SOLUTION?</h2>
          <p className="text-slate-300 text-lg mb-8">Our engineering team is ready to discuss your specific requirements</p>
          <a
            href="/quotation"
            data-testid="get-quote-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold font-oswald px-10 py-4 inline-block transition-all"
          >
            REQUEST TECHNICAL QUOTATION
          </a>
        </div>
      </section>
    </div>
  );
};

export default Services;